import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import React from 'react'
import { FaCheckDouble } from 'react-icons/fa'
import CustomButton from '../../../shared/CustomButton'
import DropdownField from '../../../shared/DropdownField'
import { useTranslation } from 'react-i18next'
import { fetchData } from '../../../helpers/fetchData'
import toast from 'react-hot-toast'
import CheckPermission from '../../../hooks/CheckPermission'
import { CANCEL_REASONS, OTHER_CANCEL_REASON } from '../../../constants/CancelReasons'

const CancelCreditInvoice = ({
  setOpenCancelModal,
  refetch,
  setSelectedInvoiceData,
  selectedInvoiceData,
  canCancelInvoice = true
}) => {
  const { hasPermission } = CheckPermission()
  const canCancel = hasPermission('billing', 'update') || hasPermission('billing', 'delete')
  const { t } = useTranslation()
  const [cancelLoading, setCancelLoading] = React.useState(false);

  const reasonOptions = CANCEL_REASONS.map(reason => ({ label: t(reason), value: reason }))

  const initialValues = {
    reason_type: '',
    other_reason: ''
  }

  const validationSchema = Yup.object().shape({
    reason_type: Yup.string().trim().required('Cancel reason is required'),
    other_reason: Yup.string().trim().when('reason_type', {
      is: OTHER_CANCEL_REASON,
      then: schema => schema.required('Cancel reason is required'),
      otherwise: schema => schema.notRequired()
    })
  })

  const handleSubmit = async data => {
    if (!canCancelInvoice) {
      toast.error('You do not have permission to cancel this invoice')
      return
    }

    const invoiceNumber =
      selectedInvoiceData?.credit_cash_invoice_number ||
      selectedInvoiceData?.invoice_number

    if (!invoiceNumber) {
      toast.error('No invoice number found')
      return
    }

    setCancelLoading(true)
    try {
      const cancel_reason =
        data?.reason_type === OTHER_CANCEL_REASON
          ? data?.other_reason?.trim()
          : data?.reason_type

      const res = await fetchData('POST', 'v3/invoices/cancelCredit', {
        credit_cash_invoice_number: invoiceNumber,
        cancel_reason
      })

      if (res?.message) {
        toast.success(res?.message)
        setSelectedInvoiceData(null)
        setOpenCancelModal(false)
        refetch()
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to cancel invoice')
    } finally {
      setCancelLoading(false)
    }
  }

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          errors,
          touched,
          handleChange,
          handleBlur,
          values
        }) => (
          <Form>
            <div className={'mb-3'}>
              <DropdownField
                name='reason_type'
                label={t('Reason for request')}
                placeholder={t('Select reason')}
                options={reasonOptions}
              />
            </div>
            {values.reason_type === OTHER_CANCEL_REASON && (
              <div className={`flex flex-col gap-1 w-full mb-3 `}>
                <label className='input-label'>{t('Cancel Reason')}</label>
                <textarea
                  placeholder={t('Enter cancel reason')}
                  className='w-full p-2 bg-gray-100 rounded-lg border border-gray-300 hover:border-primary !outline-none !shadow-none text-[14px]'
                  name='other_reason'
                  onChange={handleChange}
                  onBlur={handleBlur}
                  rows={3}
                />
                {errors.other_reason && touched.other_reason && (
                  <span className='text-sm text-red-500'>{errors.other_reason}</span>
                )}
              </div>
            )}
            {canCancel && (
              <div className='flex items-center justify-end'>
                <CustomButton
                  loading={cancelLoading}
                  type='submit'
                  icon={<FaCheckDouble size={12} className='me-2' />}
                  label={t('Cancel Invoice')}
                  buttonType='primary'
                />
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default CancelCreditInvoice
